import React, { useState, useEffect } from 'react';
import { Trophy, Brain, Sparkles, Gift, ArrowRight, CheckCircle, Lightbulb, Target, Clock, Star, Send, Zap } from 'lucide-react';

const MathsChallengeSection = () => {
  const [selectedChallenge, setSelectedChallenge] = useState(1);
  const [answer, setAnswer] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [glowPulse, setGlowPulse] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Pulsing glow effect
  useEffect(() => {
    const interval = setInterval(() => {
      setGlowPulse(prev => !prev);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const challenges = [
    {
      id: 1,
      title: "Circle Tangent Challenge",
      difficulty: "GCSE/IGCSE Higher",
      marks: 5,
      question: "A circle C has centre (2, 5). The point A (11, 8) lies on the circumference of the circle. Find the equation of the tangent to the circle at A.",
      hint: "The tangent is perpendicular to the radius at point A. Calculate the gradient of CA first, then find the perpendicular gradient for the tangent line.",
      workingShown: [
        { step: "Step 1: Find gradient of radius CA", formula: null },
        { step: null, formula: "m₁ = (8 - 5) / (11 - 2) = 3/9 = 1/3" },
        { step: "Step 2: Tangent is perpendicular to radius", formula: null },
        { step: null, formula: "m₂ = -1 ÷ (1/3) = -3" },
        { step: "Step 3: Use y - y₁ = m(x - x₁) at A(11, 8)", formula: null },
        { step: "Your turn: Complete the equation!", formula: null, highlight: true }
      ],
      image: "https://customer-assets.emergentagent.com/job_gcsemath/artifacts/0e6o4o1m_Screenshot_20260117_234953_Gallery.jpg"
    },
    {
      id: 2,
      title: "Bonus Challenge",
      difficulty: "Extension Problem",
      marks: 5,
      question: "A circle has centre (4, -2). The point B (10, 6) lies on the circumference. Find the equation of the tangent to the circle at B.",
      hint: "Same method as Challenge 1! Find the gradient from centre to B, then use the perpendicular relationship.",
      workingShown: [
        { step: "Step 1: Find gradient from (4, -2) to (10, 6)", formula: null },
        { step: null, formula: "m₁ = (6 - (-2)) / (10 - 4) = 8/6 = 4/3" },
        { step: "Step 2: Find perpendicular gradient", formula: null },
        { step: null, formula: "m₂ = -1 ÷ (4/3) = -3/4" },
        { step: "Step 3: Apply point-slope form at B(10, 6)", formula: null },
        { step: "Complete the equation!", formula: null, highlight: true }
      ],
      image: null
    }
  ];

  const currentChallenge = challenges.find(c => c.id === selectedChallenge);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!answer.trim() || !email.trim() || !name.trim()) return;

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          source: `maths_challenge_${selectedChallenge}`,
          offer: `tangent_challenge_${selectedChallenge}`,
          name,
          answer: answer.trim()
        })
      });

      if (response.ok) {
        setSubmitted(true);
      }
    } catch (error) {
      console.error('Error submitting challenge:', error);
      setSubmitted(true);
    }

    setIsSubmitting(false);
  };

  if (submitted) {
    return (
      <section id="maths-challenge" className="parallax-section" style={{
        padding: '6rem 1.5rem',
        backgroundImage: `linear-gradient(135deg, rgba(15, 23, 42, 0.94) 0%, rgba(15, 23, 42, 0.92) 100%), url('https://static.prod-images.emergentagent.com/jobs/ee1a1db2-a588-42e0-b44d-7c9f207c1d4b/images/9e105d9f3d8dcff01c003c6b30724ae7901dfd0bdd2834723dd2cafb9152565f.png')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            radial-gradient(circle at 20% 80%, rgba(20, 184, 166, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255, 213, 79, 0.1) 0%, transparent 50%)
          `,
          pointerEvents: 'none'
        }} />

        <div className="container" style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            padding: '3rem',
            border: '2px solid rgba(20, 184, 166, 0.4)',
            boxShadow: '0 0 60px rgba(20, 184, 166, 0.2)'
          }}>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #C5A059, #C5A059)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 0 40px rgba(20, 184, 166, 0.5)',
              animation: 'successBounce 1s ease-out'
            }}>
              <CheckCircle size={50} color="white" />
            </div>
            
            <h3 style={{ 
              color: 'white', 
              fontSize: 'clamp(1.5rem, 4vw, 2rem)', 
              marginBottom: '1rem',
              fontWeight: 700
            }}>
              Challenge Submitted! 🎉
            </h3>
            
            <p style={{ 
              color: 'rgba(255, 255, 255, 0.9)', 
              fontSize: '1.1rem',
              marginBottom: '1.5rem',
              lineHeight: 1.6
            }}>
              Brilliant effort, <span style={{ color: '#C5A059', fontWeight: 700 }}>{name}</span>! 
              <br />Dr. Michael will personally review your answer.
            </p>

            <div style={{
              background: 'linear-gradient(135deg, rgba(255, 213, 79, 0.2), rgba(255, 213, 79, 0.1))',
              border: '2px solid #E5C575',
              borderRadius: '16px',
              padding: '1.5rem',
              marginBottom: '1.5rem'
            }}>
              <Gift size={32} color="#E5C575" style={{ marginBottom: '0.75rem' }} />
              <p style={{ color: '#E5C575', fontWeight: 700, margin: 0, fontSize: '1.1rem' }}>
                Your FREE 30-minute lesson awaits!
              </p>
              <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem', margin: '0.5rem 0 0 0' }}>
                If your answer is correct
              </p>
            </div>

            <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.9rem' }}>
              We'll email you at <span style={{ color: '#C5A059' }}>{email}</span> within 24 hours.
            </p>
          </div>
        </div>

        <style>{`
          @keyframes successBounce {
            0% { transform: scale(0); }
            50% { transform: scale(1.2); }
            100% { transform: scale(1); }
          }
        `}</style>
      </section>
    );
  }

  return (
    <section id="maths-challenge" className="parallax-section" style={{
      padding: '6rem 1.5rem',
      backgroundImage: `linear-gradient(135deg, rgba(15, 23, 42, 0.94) 0%, rgba(15, 23, 42, 0.92) 100%), url('https://static.prod-images.emergentagent.com/jobs/ee1a1db2-a588-42e0-b44d-7c9f207c1d4b/images/9e105d9f3d8dcff01c003c6b30724ae7901dfd0bdd2834723dd2cafb9152565f.png')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Animated gradient background */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `
          radial-gradient(circle at 10% 20%, rgba(20, 184, 166, 0.12) 0%, transparent 40%),
          radial-gradient(circle at 90% 80%, rgba(255, 213, 79, 0.08) 0%, transparent 40%),
          radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.06) 0%, transparent 50%)
        `,
        pointerEvents: 'none'
      }} />

      {/* Floating math symbols */}
      <div className="floating-symbol" style={{ top: '8%', left: '3%', fontSize: '5rem', color: 'rgba(255, 213, 79, 0.08)' }}>π</div>
      <div className="floating-symbol" style={{ top: '55%', right: '5%', fontSize: '4rem', color: 'rgba(20, 184, 166, 0.1)', animationDelay: '1s' }}>∑</div>
      <div className="floating-symbol" style={{ bottom: '12%', left: '8%', fontSize: '3.5rem', color: 'rgba(139, 92, 246, 0.08)', animationDelay: '0.5s' }}>√</div>
      <div className="floating-symbol" style={{ top: '25%', right: '15%', fontSize: '4rem', color: 'rgba(255, 255, 255, 0.04)', animationDelay: '2s' }}>∫</div>
      <div className="floating-symbol" style={{ bottom: '30%', right: '25%', fontSize: '3rem', color: 'rgba(20, 184, 166, 0.06)', animationDelay: '1.5s' }}>θ</div>

      <div className="container" style={{ maxWidth: '1100px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        {/* Epic Header */}
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.75rem',
            background: 'linear-gradient(90deg, rgba(255, 213, 79, 0.25), rgba(139, 92, 246, 0.2))',
            padding: '0.75rem 1.75rem',
            borderRadius: '9999px',
            marginBottom: '1.5rem',
            border: '2px solid rgba(255, 213, 79, 0.4)',
            boxShadow: glowPulse ? '0 0 30px rgba(255, 213, 79, 0.3)' : '0 0 15px rgba(255, 213, 79, 0.15)',
            transition: 'box-shadow 0.5s ease'
          }}>
            <Trophy size={24} color="#E5C575" />
            <span style={{ 
              color: '#E5C575', 
              fontWeight: 800, 
              fontSize: '1rem',
              letterSpacing: '1px',
              textTransform: 'uppercase'
            }}>
              Maths Challenge
            </span>
            <Zap size={20} color="#E5C575" />
          </div>

          <h2 style={{ 
            color: 'white', 
            fontSize: 'clamp(2rem, 5vw, 3.5rem)', 
            fontWeight: 800,
            marginBottom: '1rem',
            lineHeight: 1.1,
            textShadow: '0 4px 30px rgba(0, 0, 0, 0.3)'
          }}>
            Can You Crack
            <br />
            <span style={{ 
              background: 'linear-gradient(90deg, #C5A059, #E5C575, #0F172A)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              The Tangent Code?
            </span>
          </h2>

          <p style={{ 
            color: 'rgba(255, 255, 255, 0.85)', 
            fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
            maxWidth: '650px',
            margin: '0 auto',
            lineHeight: 1.7
          }}>
            Solve this GCSE Higher Tier problem and win a 
            <span style={{ color: '#E5C575', fontWeight: 700 }}> FREE 30-minute lesson </span>
            with Dr. Michael!
          </p>
        </div>

        {/* Prize Banner */}
        <div style={{
          background: 'linear-gradient(90deg, rgba(16, 185, 129, 0.2), rgba(20, 184, 166, 0.15))',
          border: '2px solid rgba(16, 185, 129, 0.5)',
          borderRadius: '20px',
          padding: '1.5rem 2rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1.25rem',
          marginBottom: '2.5rem',
          flexWrap: 'wrap',
          textAlign: 'center',
          boxShadow: '0 10px 40px rgba(16, 185, 129, 0.15)'
        }}>
          <div style={{
            width: '55px',
            height: '55px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #C5A059, #A88B4A)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 0 20px rgba(16, 185, 129, 0.4)'
          }}>
            <Gift size={28} color="white" />
          </div>
          <div>
            <span style={{ 
              color: 'white', 
              fontSize: 'clamp(1.1rem, 2.5vw, 1.4rem)',
              fontWeight: 700,
              display: 'block'
            }}>
              PRIZE: FREE 30-Min 1:1 Lesson
            </span>
            <span style={{ color: '#C5A059', fontSize: '0.95rem', fontWeight: 600 }}>
              Worth £35 — Personally with Dr. Michael!
            </span>
          </div>
          <Sparkles size={28} color="#E5C575" />
        </div>

        {/* Challenge Tabs */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          marginBottom: '2rem',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          {challenges.map((challenge) => (
            <button
              key={challenge.id}
              onClick={() => {
                setSelectedChallenge(challenge.id);
                setShowHint(false);
                setAnswer('');
              }}
              style={{
                padding: '1rem 1.75rem',
                borderRadius: '14px',
                border: selectedChallenge === challenge.id 
                  ? '2px solid #C5A059' 
                  : '2px solid rgba(255, 255, 255, 0.15)',
                background: selectedChallenge === challenge.id 
                  ? 'linear-gradient(135deg, rgba(20, 184, 166, 0.25), rgba(20, 184, 166, 0.1))' 
                  : 'rgba(255, 255, 255, 0.03)',
                color: selectedChallenge === challenge.id ? 'white' : 'rgba(255, 255, 255, 0.6)',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                boxShadow: selectedChallenge === challenge.id 
                  ? '0 8px 25px rgba(20, 184, 166, 0.25)' 
                  : 'none'
              }}
              data-testid={`challenge-tab-${challenge.id}`}
            >
              {challenge.id === 1 ? <Target size={20} /> : <Star size={20} />}
              <span>{challenge.title}</span>
              {challenge.id === 1 && (
                <span style={{
                  background: '#C5A059',
                  color: 'white',
                  fontSize: '0.7rem',
                  padding: '0.2rem 0.5rem',
                  borderRadius: '9999px',
                  fontWeight: 800
                }}>HOT</span>
              )}
            </button>
          ))}
        </div>

        {/* Main Content Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '2rem',
          alignItems: 'start'
        }}>
          {/* Challenge Card */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.04)',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            padding: 'clamp(1.5rem, 4vw, 2rem)',
            border: '1px solid rgba(255, 255, 255, 0.08)'
          }}>
            {/* Challenge Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '1.5rem',
              flexWrap: 'wrap',
              gap: '1rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  width: '52px',
                  height: '52px',
                  borderRadius: '14px',
                  background: 'linear-gradient(135deg, #C5A059, #C5A059)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 20px rgba(20, 184, 166, 0.3)'
                }}>
                  <Brain size={28} color="white" />
                </div>
                <div>
                  <h3 style={{ color: 'white', fontSize: '1.15rem', fontWeight: 700, margin: 0 }}>
                    {currentChallenge.title}
                  </h3>
                  <span style={{ color: '#C5A059', fontSize: '0.85rem', fontWeight: 600 }}>
                    {currentChallenge.difficulty}
                  </span>
                </div>
              </div>
              <div style={{
                background: 'rgba(139, 92, 246, 0.2)',
                padding: '0.6rem 1.1rem',
                borderRadius: '9999px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                border: '1px solid rgba(139, 92, 246, 0.3)'
              }}>
                <Clock size={16} color="#0F172A" />
                <span style={{ color: '#0F172A', fontWeight: 700, fontSize: '0.9rem' }}>
                  {currentChallenge.marks} marks
                </span>
              </div>
            </div>

            {/* Question Box */}
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '1.5rem',
              marginBottom: '1.5rem',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
            }}>
              <p style={{ 
                color: '#1E293B', 
                fontSize: 'clamp(1rem, 2.5vw, 1.1rem)',
                fontWeight: 500,
                lineHeight: 1.7,
                margin: 0
              }}>
                <span style={{ 
                  color: 'white', 
                  background: 'linear-gradient(135deg, #C5A059, #C5A059)',
                  fontWeight: 800,
                  padding: '0.2rem 0.6rem',
                  borderRadius: '6px',
                  marginRight: '0.5rem',
                  fontSize: '0.9rem'
                }}>
                  Q
                </span>
                {currentChallenge.question}
              </p>
            </div>

            {/* Problem Image */}
            {currentChallenge.image && (
              <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: '0.75rem',
                marginBottom: '1.5rem',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
              }}>
                <p style={{ 
                  color: '#64748B', 
                  fontSize: '0.85rem', 
                  marginBottom: '0.75rem',
                  fontWeight: 600,
                  textAlign: 'center'
                }}>
                  📐 Dr. Michael's Working — Complete the solution!
                </p>
                <img 
                  src={currentChallenge.image} 
                  alt="Maths problem with working"
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    borderRadius: '12px',
                    display: 'block'
                  }}
                />
              </div>
            )}

            {/* Working Steps */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(20, 184, 166, 0.12), rgba(20, 184, 166, 0.05))',
              borderRadius: '16px',
              padding: '1.5rem',
              marginBottom: '1.5rem',
              border: '1px solid rgba(20, 184, 166, 0.25)'
            }}>
              <p style={{ 
                color: '#C5A059', 
                fontWeight: 800, 
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '1rem'
              }}>
                <Sparkles size={20} />
                Working Steps:
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                {currentChallenge.workingShown.map((item, index) => (
                  <div 
                    key={index}
                    style={{
                      color: item.highlight 
                        ? '#E5C575' 
                        : item.formula 
                          ? 'white' 
                          : 'rgba(255, 255, 255, 0.85)',
                      fontSize: item.formula ? '1rem' : '0.95rem',
                      fontFamily: item.formula ? 'monospace' : 'inherit',
                      fontWeight: item.highlight ? 700 : item.formula ? 600 : 400,
                      padding: item.formula ? '0.5rem 0.75rem' : '0.25rem 0',
                      background: item.formula ? 'rgba(0, 0, 0, 0.2)' : 'transparent',
                      borderRadius: item.formula ? '8px' : '0',
                      borderLeft: item.highlight ? '3px solid #E5C575' : 'none',
                      paddingLeft: item.highlight ? '0.75rem' : undefined
                    }}
                  >
                    {item.step || item.formula}
                  </div>
                ))}
              </div>
            </div>

            {/* Hint Button */}
            <button
              onClick={() => setShowHint(!showHint)}
              style={{
                background: showHint 
                  ? 'rgba(255, 213, 79, 0.2)' 
                  : 'rgba(255, 213, 79, 0.08)',
                border: '1px solid rgba(255, 213, 79, 0.35)',
                borderRadius: '12px',
                padding: '0.9rem 1.5rem',
                color: '#E5C575',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                width: '100%',
                justifyContent: 'center',
                transition: 'all 0.3s ease'
              }}
              data-testid="hint-toggle"
            >
              <Lightbulb size={20} />
              {showHint ? 'Hide Hint' : 'Need a Hint? 💡'}
            </button>

            {/* Hint Content */}
            {showHint && (
              <div style={{
                background: 'rgba(255, 213, 79, 0.12)',
                borderRadius: '12px',
                padding: '1.25rem',
                marginTop: '1rem',
                border: '1px dashed rgba(255, 213, 79, 0.5)',
                animation: 'slideDown 0.3s ease'
              }}>
                <p style={{ 
                  color: 'rgba(255, 255, 255, 0.95)', 
                  margin: 0,
                  lineHeight: 1.6,
                  fontSize: '0.95rem'
                }}>
                  <strong style={{ color: '#E5C575' }}>💡 HINT:</strong> {currentChallenge.hint}
                </p>
              </div>
            )}
          </div>

          {/* Answer Submission Form */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(20, 184, 166, 0.1), rgba(139, 92, 246, 0.08))',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            padding: 'clamp(1.5rem, 4vw, 2rem)',
            border: '2px solid rgba(20, 184, 166, 0.25)',
            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ 
              color: 'white', 
              fontSize: '1.4rem', 
              fontWeight: 800,
              marginBottom: '1.5rem',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}>
              <Send size={22} />
              Submit Your Answer
            </h3>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ 
                  color: 'rgba(255, 255, 255, 0.9)', 
                  fontWeight: 600,
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontSize: '0.95rem'
                }}>
                  Your Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your name"
                  required
                  className="challenge-input"
                  style={{
                    width: '100%',
                    padding: '1rem 1.25rem',
                    borderRadius: '12px',
                    border: '2px solid rgba(255, 255, 255, 0.15)',
                    background: 'rgba(255, 255, 255, 0.08)',
                    color: 'white',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'all 0.3s ease',
                    boxSizing: 'border-box'
                  }}
                  data-testid="challenge-name-input"
                />
              </div>

              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ 
                  color: 'rgba(255, 255, 255, 0.9)', 
                  fontWeight: 600,
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontSize: '0.95rem'
                }}>
                  Your Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  required
                  className="challenge-input"
                  style={{
                    width: '100%',
                    padding: '1rem 1.25rem',
                    borderRadius: '12px',
                    border: '2px solid rgba(255, 255, 255, 0.15)',
                    background: 'rgba(255, 255, 255, 0.08)',
                    color: 'white',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'all 0.3s ease',
                    boxSizing: 'border-box'
                  }}
                  data-testid="challenge-email-input"
                />
              </div>

              <div style={{ marginBottom: '1.75rem' }}>
                <label style={{ 
                  color: 'rgba(255, 255, 255, 0.9)', 
                  fontWeight: 600,
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontSize: '0.95rem'
                }}>
                  Your Answer <span style={{ color: '#E5C575' }}>(y = mx + c)</span>
                </label>
                <input
                  type="text"
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder="e.g., y = -3x + 41"
                  required
                  style={{
                    width: '100%',
                    padding: '1.25rem',
                    borderRadius: '14px',
                    border: '3px solid rgba(20, 184, 166, 0.5)',
                    background: 'white',
                    color: '#1E293B',
                    fontSize: '1.2rem',
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    outline: 'none',
                    transition: 'all 0.3s ease',
                    boxSizing: 'border-box',
                    textAlign: 'center'
                  }}
                  data-testid="challenge-answer-input"
                />
                <p style={{ 
                  color: 'rgba(255, 255, 255, 0.5)', 
                  fontSize: '0.8rem',
                  marginTop: '0.5rem',
                  textAlign: 'center'
                }}>
                  Write your equation in the form y = mx + c
                </p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || !answer.trim() || !email.trim() || !name.trim()}
                style={{
                  width: '100%',
                  padding: '1.25rem 2rem',
                  borderRadius: '14px',
                  border: 'none',
                  background: isSubmitting 
                    ? 'rgba(255, 255, 255, 0.2)' 
                    : 'linear-gradient(135deg, #C5A059, #C5A059, #A88B4A)',
                  color: 'white',
                  fontSize: '1.15rem',
                  fontWeight: 800,
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.75rem',
                  transition: 'all 0.3s ease',
                  boxShadow: isSubmitting ? 'none' : '0 8px 30px rgba(20, 184, 166, 0.4)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
                data-testid="challenge-submit-btn"
              >
                {isSubmitting ? (
                  <>Submitting...</>
                ) : (
                  <>
                    Submit & Claim Prize
                    <ArrowRight size={22} />
                  </>
                )}
              </button>
            </form>

            <p style={{ 
              color: 'rgba(255, 255, 255, 0.5)', 
              fontSize: '0.85rem',
              textAlign: 'center',
              marginTop: '1.25rem',
              lineHeight: 1.5
            }}>
              Dr. Michael reviews all submissions personally.
              <br />Good luck! 🍀✨
            </p>
          </div>
        </div>

        {/* Bottom Encouragement */}
        <div style={{
          marginTop: '3rem',
          textAlign: 'center',
          padding: '1.75rem 2rem',
          background: 'linear-gradient(90deg, rgba(13, 148, 136, 0.15), rgba(139, 92, 246, 0.1))',
          borderRadius: '20px',
          border: '1px solid rgba(13, 148, 136, 0.25)'
        }}>
          <Brain size={28} color="#C5A059" style={{ marginBottom: '0.75rem' }} />
          <p style={{ color: 'white', fontSize: '1.1rem', margin: 0, fontWeight: 600 }}>
            This is a <span style={{ color: '#C5A059' }}>real GCSE Higher Tier question!</span>
          </p>
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.95rem', margin: '0.5rem 0 0 0' }}>
            Topics like this are covered in Dr. Michael's weekly Higher Maths sessions.
          </p>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        .floating-symbol {
          position: absolute;
          font-weight: 800;
          pointer-events: none;
          animation: float 8s ease-in-out infinite;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-25px) rotate(8deg); }
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .challenge-input::placeholder {
          color: rgba(255, 255, 255, 0.4);
        }
        .challenge-input:focus {
          border-color: #C5A059 !important;
          background: rgba(255, 255, 255, 0.12) !important;
        }
      `}</style>
    </section>
  );
};

export default MathsChallengeSection;
